import React, { useState, useEffect } from 'react';
import { io } from 'socket.io-client';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  Paper,
  Button,
  Chip,
  Divider,
} from '@mui/material';
import {
  Chat,
  People,
  Inventory,
  MenuBook,
  TrendingUp,
  FiberManualRecord,
  AssessmentOutlined,
} from '@mui/icons-material';
import { conversationsAPI, statusAPI } from '../../services/api';
import config from '../../config/config';
import CompanyReport from '../../components/CompanyReport/CompanyReport';

const Dashboard = () => {
  const user = useSelector((state) => state.auth.user);
  const isSuperAdmin = user?.role === 'super_admin';
  const isCompanyAdmin = user?.role === 'company_admin' || isSuperAdmin;
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    totalConversations: 0,
    activeToday: 0,
    uniqueUsers: 0
  });
  const [recentChats, setRecentChats] = useState([]);
  const [systemStatus, setSystemStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [socketConnected, setSocketConnected] = useState(false);
  const [todayUniqueCustomers, setTodayUniqueCustomers] = useState(new Set());

  useEffect(() => {
    fetchDashboardData();

    // Initialize Socket.io connection
    console.log('🔌 Attempting to connect to Socket.io at:', config.SOCKET_URL);
    const socket = io(config.SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });

    // Socket.io event listeners
    socket.on('connect', () => {
      console.log('✅ Socket.io connected successfully!');
      console.log('   Socket ID:', socket.id);
      console.log('   Transport:', socket.io.engine.transport.name);
      setSocketConnected(true);
    });

    socket.on('connect_error', (error) => {
      console.error('❌ Socket.io connection error:', error.message);
      console.error('   URL:', config.SOCKET_URL);
      console.error('   Error details:', error);
      setSocketConnected(false);
    });

    socket.on('disconnect', (reason) => {
      console.log('📡 Socket.io disconnected. Reason:', reason);
      setSocketConnected(false);
    });

    // Listen for new conversations
    socket.on('newConversation', (newConv) => {
      console.log('📨 New conversation received:', newConv);

      // Add to recent chats
      setRecentChats(prevChats => [newConv, ...prevChats.slice(0, 9)]);

      // Update stats with live tracking
      setStats(prevStats => {
        const today = new Date().toDateString();
        const isToday = new Date(newConv.created_at).toDateString() === today;

        return {
          totalConversations: prevStats.totalConversations + 1,
          activeToday: prevStats.activeToday,  // Will be updated below
          uniqueUsers: prevStats.uniqueUsers   // Will be updated below
        };
      });

      // Update today's unique customers if message is from today
      const isToday = new Date(newConv.created_at).toDateString() === new Date().toDateString();
      if (isToday && newConv.customer_id) {
        setTodayUniqueCustomers(prevCustomers => {
          const updatedSet = new Set(prevCustomers);
          const wasNew = !updatedSet.has(newConv.customer_id);
          updatedSet.add(newConv.customer_id);

          // Update activeToday count if it's a new customer
          if (wasNew) {
            setStats(prevStats => ({
              ...prevStats,
              activeToday: updatedSet.size
            }));
          }

          return updatedSet;
        });
      }
    });

    // Cleanup on unmount
    return () => {
      console.log('🔌 Cleaning up Socket.io connection');
      socket.off('connect');
      socket.off('connect_error');
      socket.off('disconnect');
      socket.off('newConversation');
      socket.disconnect();
    };
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [conversationsRes, statsRes, statusRes] = await Promise.all([
        conversationsAPI.getAll(10),
        conversationsAPI.getStats(),
        statusAPI.check()
      ]);

      const conversations = conversationsRes.data.data || [];
      setRecentChats(conversations.slice(0, 10));

      const statsData = statsRes.data?.data || {};

      setStats({
        totalConversations: Number(statsData.totalMessages || 0),
        activeToday: Number(statsData.activeToday || 0),
        uniqueUsers: Number(statsData.uniqueUsers || 0)
      });

      // Keep today's customer set in sync for live incremental updates.
      setTodayUniqueCustomers(new Set());

      setSystemStatus(statusRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  const statsCards = [
    {
      title: 'Total Messages',
      value: stats.totalConversations,
      icon: <Chat sx={{ fontSize: 40 }} />,
      color: '#1976d2',
      bgcolor: '#e3f2fd',
    },
    {
      title: 'Active Today',
      value: stats.activeToday,
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      color: '#7b1fa2',
      bgcolor: '#f3e5f5',
    },
    {
      title: 'Unique Users',
      value: stats.uniqueUsers,
      icon: <People sx={{ fontSize: 40 }} />,
      color: '#388e3c',
      bgcolor: '#e8f5e9',
    },
    {
      title: 'System Status',
      value: systemStatus?.database === 'connected' ? 'Online' : 'Offline',
      icon: <Inventory sx={{ fontSize: 40 }} />,
      color: systemStatus?.database === 'connected' ? '#388e3c' : '#d32f2f',
      bgcolor: systemStatus?.database === 'connected' ? '#e8f5e9' : '#ffebee',
    },
  ];

  const quickActions = [
    { label: 'Manage Instructions', icon: <MenuBook />, href: '/instructions' },
    { label: 'Manage Staff', icon: <People />, href: '/staff' },
    { label: 'Manage Products', icon: <Inventory />, href: '/items' },
    { label: 'View All Chats', icon: <Chat />, href: '/conversations' },
  ];

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Welcome to W.H.D. an IT Solution Provider Chatbot Admin
          </Typography>
        </Box>
        <Chip
          icon={<FiberManualRecord sx={{ fontSize: 12 }} />}
          label={socketConnected ? 'Live Updates Active' : 'Connecting...'}
          color={socketConnected ? 'success' : 'default'}
          size="small"
          sx={{ px: 1 }}
        />
      </Box>

      {/* Company-Wise Report - Only for Super Admin */}
      {isSuperAdmin && (
        <>
          <CompanyReport />
          <Divider sx={{ my: 4 }} />
        </>
      )}

      {/* Customer Summary Button - Only for Company Admin */}
      {isCompanyAdmin && (
        <Box sx={{ mb: 4 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: '#e3f2fd', width: 60, height: 60 }}>
                    <AssessmentOutlined sx={{ fontSize: 32, color: '#1976d2' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Customer Summary Report
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      AI-powered customer insights with priority levels and actionable summaries
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<AssessmentOutlined />}
                  onClick={() => navigate('/customer-summary')}
                  sx={{ minWidth: 180 }}
                >
                  View Report
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsCards.map((stat, index) => (
          <Grid item size={3} key={index}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: stat.bgcolor, width: 60, height: 60 }}>
                    {React.cloneElement(stat.icon, { sx: { color: stat.color } })}
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Recent Conversations
            </Typography>
            <List>
              {recentChats.length > 0 ? (
                recentChats.map((chat, index) => (
                  <ListItem
                    key={index}
                    alignItems="flex-start"
                    sx={{
                      bgcolor: 'grey.50',
                      mb: 1,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'grey.200',
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {chat.customer_name?.charAt(0) || 'U'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {chat.customer_name || 'Unknown User'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {chat.customer_id}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.primary">
                            {chat.message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {new Date(chat.created_at).toLocaleString()}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))
              ) : (
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 5 }}>
                  No conversations yet
                </Typography>
              )}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {quickActions.map((action, index) => (
                <Button
                  key={index}
                  variant="outlined"
                  fullWidth
                  href={action.href}
                  startIcon={action.icon}
                  sx={{
                    justifyContent: 'flex-start',
                    py: 1.5,
                    textTransform: 'none',
                    fontSize: '0.95rem',
                  }}
                >
                  {action.label}
                </Button>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
